import { Dispatch } from 'redux';
import { Services } from '../../types/services';
import toast from 'react-hot-toast';
import React from 'react';

const FETCH_RELATIONS_REQUEST = 'FETCH_RELATIONS_REQUEST';
const GET_RELATIONS = 'GET_RELATIONS';
const RELATIONS_ERROR = 'RELATIONS_ERROR';

interface Relation {
  id: string;
  [key: string]: unknown;
}

interface State {
  relations: Relation[];
  loading: boolean;
  error: string;
}

const initialState: State = {
  relations: [],
  loading: true,
  error: '',
};

interface Action {
  type: typeof FETCH_RELATIONS_REQUEST | typeof GET_RELATIONS | typeof RELATIONS_ERROR;
  payload?: Relation[] | string;
}

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case FETCH_RELATIONS_REQUEST:
      return {
        ...state,
        loading: true,
      };
    case GET_RELATIONS:
      return {
        ...state,
        relations: action.payload as Relation[],
        loading: false,
        error: '',
      };
    case RELATIONS_ERROR:
      return {
        ...state,
        relations: [],
        loading: false,
        error: action.payload as string,
      };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosService = services.axios.Axios();

  const getPracticeRelations = (practiceId: string) => async () =>
    axiosService.get('practices/' + practiceId + '/relations');

  const getPracticeRelationbyOfferingId = (
    partnerOfferingId: string,
    partnerCustomId = '',
    convertAttachments: boolean
  ) => async () =>
    axiosService.get(
      `practices/${partnerOfferingId}/relations/detail?partnerCustomId=${partnerCustomId}&convertAttachments=${convertAttachments}`
    );

  const fetchRelations = (practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch({ type: FETCH_RELATIONS_REQUEST });
        const response = await axiosService.get<{ data: Relation[] }>(
          `practices/${practiceId}/relations`
        );
        const relations = response.data.data;
        dispatch({ type: GET_RELATIONS, payload: relations });
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: RELATIONS_ERROR, payload: errorMsg });
      }
    };
  };

  const saveRelation = (
    practiceId: string,
    partnerOfferingId: string,
    customPartnerId = '',
    convertAttachments = false
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosService.post(
          `Practices/${practiceId}/relations?offeringId=${partnerOfferingId}&customPartnerId=${customPartnerId}&convertAttachments=${convertAttachments}`
        );
        return response.data;
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: RELATIONS_ERROR, payload: errorMsg });

        toast(
          () =>
            React.createElement(
              'div',
              { className: 'text-light d-flex justify-content-between w-100' },
              'Error: Something occurred during save relations!'
            ),
          {
            duration: 7500,
            style: {
              minWidth: '25vw',
              background: 'red',
            },
          }
        );
      }
    };
  };

  const updateRelation = (
    practiceId: string,
    partnerOfferingId: string,
    customPartnerId = '',
    convertAttachments = false
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosService.patch(
          `Practices/${practiceId}/relations?offeringId=${partnerOfferingId}&customPartnerId=${customPartnerId}&convertAttachments=${convertAttachments}`
        );
        return response.data;
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: RELATIONS_ERROR, payload: errorMsg });
      }
    };
  };

  const deletePracticeOffering = (relationId: string, practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        const response = await axiosService.delete<{ data: unknown }>(`relations/${relationId}`);
        dispatch(fetchRelations(practiceId) as unknown as Parameters<typeof dispatch>[0]);
        return response.data.data;
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: RELATIONS_ERROR, payload: errorMsg });
      }
    };
  };

  return {
    deletePracticeOffering,
    fetchRelations,
    getPracticeRelations,
    getPracticeRelationbyOfferingId,
    saveRelation,
    updateRelation,
  };
};

